import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { UrlTree } from '@angular/router';
import { delay } from 'rxjs/operators';
import { maxOver, minOver, MultiAggregator, noAgg, noAgg0, SingleAggregator, weightedAverage } from '../../services/aggregation';
import {
	BasicFilterExpression,
	CijferMeasure,
	CompoundFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	ExportDataOptions,
	ExportFilter,
	FilterExpression,
} from '../../services/data.service';
import { att } from '../../services/measures';
import { ColumnDef } from '../../shared/components/table/table/table.model';
import { DRIE_SCHOOLJAREN_EXCL_2020, generateCssClassForString, getOnderwijsresultatenSchooljaarRangeString } from '@cumlaude/shared-utils';
import { get, initial, last, range } from 'lodash-es';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { Observable } from 'rxjs';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { BarInfo } from '../../services/stacked-bars';
import { formatNumber } from '@angular/common';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { Axis } from '../../services/axis';
import { DashboardVariant } from '../../services/weergave-opties';
import { Att, AttrPath, Examenstatus, Table } from '@cumlaude/metadata';
import { indicatorOverOpties, SchooljaarIndicatorOverComponent } from '../../schooljaar-indicator-over/schooljaar-indicator-over.component';
import { TooltipType } from '@cumlaude/shared-components-overlays';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { OnderwijsresultatenUitsluitenFilterComponent } from '../../onderwijsresultaten-uitsluiten-filter/onderwijsresultaten-uitsluiten-filter.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { DataTreeTableConfig } from '../../shared/dashboard/data-tree-table/data-tree-table-config';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

const permanentTable: AttrPath[] = [['cf_fk_ilt', 'ilt_nm_niveau']];

export interface CijferSeCeI extends Attributes {
	cf_m_nr_cijfer: number;
	cf_m_nr_cijfer_ce: number;
	cf_m_nr_cijferverschil_se_ce: number;
	cf_m_nr_leerlingen: number;
	/** Weergave Verschil: Histogram met aantallen per afgerond verschil */
	cf_m_nr_verschil_minus_3_75: number; // < -3,75
	cf_m_nr_verschil_minus_3_5: number;
	cf_m_nr_verschil_minus_3: number;
	cf_m_nr_verschil_minus_2_5: number;
	cf_m_nr_verschil_minus_2: number;
	cf_m_nr_verschil_minus_1_5: number;
	cf_m_nr_verschil_minus_1: number;
	cf_m_nr_verschil_0: number;
	cf_m_nr_verschil_0_5: number;
	cf_m_nr_verschil_1: number;
	cf_m_nr_verschil_1_5: number;
	cf_m_nr_verschil_2: number;
	cf_m_nr_verschil_2_5: number;
	cf_m_nr_verschil_3: number;
	cf_m_nr_verschil_3_5: number;
	cf_m_nr_verschil_3_75: number; // >= +3,75
	/** Weergave CE of SE: Histogram met aantallen per afgerond cijfer */
	cf_m_nr_aantal_0: number;
	cf_m_nr_aantal_1: number;
	cf_m_nr_aantal_2: number;
	cf_m_nr_aantal_3: number;
	cf_m_nr_aantal_4: number;
	cf_m_nr_aantal_5: number;
	cf_m_nr_aantal_6: number;
	cf_m_nr_aantal_7: number;
	cf_m_nr_aantal_8: number;
	cf_m_nr_aantal_9: number;
	cf_m_nr_aantal_10: number;
}

export interface CijferSeCeA extends Attributes {
	cijferVerschilSeCe: number | null;
	cijferSe: number | null;
	cijferCe: number | null;
	verschilAfgerond: number | null;
	cijferAfgerond: number;
	minVerschil: number;
	maxVerschil: number;
	leerlingen: number;
}

// aantal CE-cijfers voor het betreffende schooljaar voor de betreffende bekostigingings-BRIN moet >= 30 zijn
const uitsluitenFilter = new BasicFilterExpression(['cf_fk_or_cvcl', 'or_q_vakken'], 30, '>=');

const permanentFilters: FilterExpression[] = [
	new BasicFilterExpression(['cf_is_diplomavak'], 1),
	// Combinatiecijfervakken HAVO/VWO niet tonen, beroepsgerichte vakken VMBO wel
	new CompoundFilterExpression(
		[
			new BasicFilterExpression(['cf_is_combinatiecijfervak'], 0),
			new BasicFilterExpression(['cf_fk_ilt', 'ilt_nm_niveau'], ['HAVO', 'VWO'], 'not in'),
		],
		'or'
	),
	new BasicFilterExpression(['cf_nm_kolomtype'], 'SE-cijfer'),
	new BasicFilterExpression(['cf_fun_nm_examenstatus_met_prognose'], [Examenstatus.GESLAAGD, Examenstatus.AFGEWEZEN], 'in'),
	new CompoundFilterExpression(
		[new BasicFilterExpression(['cf_fk_vk', 'vk_co_vak'], 1107, '<>'), new BasicFilterExpression(['cf_nm_schooljaar'], '2015/2016', '<')],
		'or'
	),
];

@Component({
	selector: 'app-verschil-se-ce',
	templateUrl: './verschil-se-ce.component.html',
	styleUrls: ['./verschil-se-ce.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		SchooljaarIndicatorOverComponent,
		OnderwijsresultatenUitsluitenFilterComponent,
		BarchartTableComponent,
		FilterBarComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerschilSeCeComponent extends BarchartTableConfig<CijferSeCeI, CijferSeCeA> implements AfterViewInit {
	protected readonly dataService = inject(DataService);

	table: AttrPath[] = permanentTable;

	override fixedBeforeGroups = 1;

	override flexibleGroupsRemovable = false;

	groups = computed(() => {
		const huidigeGroeperingen = this.groeperingService.huidigeGroeperingen();
		return this.variant() === DashboardVariant.ACTUEEL ? huidigeGroeperingen : [...huidigeGroeperingen, ['cf_nm_schooljaar'] as AttrPath];
	});

	initialFilters: FilterName[] = [
		'x_onderwijsresultaten_cf_schooljaar',
		'cf_fk_br_vest.br_co_brin',
		'cf_fk_ilt.ilt_nm_niveau',
		'cf_nm_vak',
		'cf_fk_vk.vk_nm_kernvak',
		'cf_nm_excijf_uitzondering',
		'x_cijfer_metzonder_ce',
	];

	variant = this.qp.signal('variant');

	schooljaar?: string;

	aantalJaren = DRIE_SCHOOLJAREN_EXCL_2020;

	uitsluiten = this.qp.signal('or-uitsluiten');

	permanentExpressions = computed(() => {
		return this.uitsluiten() ? [...permanentFilters, uitsluitenFilter] : permanentFilters;
	});

	override ngAfterViewInit() {
		super.ngAfterViewInit();
		this.subscriptions.push(
			this.filterService
				.observe('x_onderwijsresultaten_cf_schooljaar')
				.pipe(delay(0))
				.subscribe(([schooljaar, aantalJaren]) => {
					this.schooljaar = schooljaar;
					this.aantalJaren = aantalJaren;
				})
		);
	}

	protected override getFixedAfterGroups(): number {
		return this.variant() === DashboardVariant.ACTUEEL ? 0 : 1;
	}

	override getAlternatingGroupLevel(): number {
		return this.variant() === DashboardVariant.ACTUEEL ? 1 : 2;
	}

	factTable = Table.fac_cf_cijfer;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getCijfersData({ ...options, m: [CijferMeasure.SE_CE], r: this.getRollupLevels(options) });
	}

	protected getRollupLevels({ g }: DataOptions): number[] | undefined {
		return range(0, g!.length);
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getCijfersExportData(options);
	}

	protected override multiAggregators: MultiAggregator<keyof CijferSeCeA, CijferSeCeI, CijferSeCeA, number | null>[] = [
		weightedAverage('cijferSe', 'cf_m_nr_cijfer'),
		weightedAverage('cijferCe', 'cf_m_nr_cijfer_ce'),
	];

	protected override singleAggregators: Partial<{ [ai in keyof CijferSeCeA]: SingleAggregator<CijferSeCeI, CijferSeCeA[ai]> }> = {
		leerlingen: noAgg0('cf_m_nr_leerlingen'),
		cijferVerschilSeCe: noAgg('cf_m_nr_cijferverschil_se_ce'),
		minVerschil: minOver('cf_m_nr_cijferverschil_se_ce'),
		maxVerschil: maxOver('cf_m_nr_cijferverschil_se_ce'),
	};

	override createMeasureColumns(context: DashboardContext<CijferSeCeI, CijferSeCeA, VerschilSeCeComponent>): ColumnDef<DataRow<CijferSeCeA>>[] {
		return [
			createMeasureColumn('SE-CE', att('cijferVerschilSeCe'), { context, format: '+1.2-2' }),
			createMeasureColumn('SE', att('cijferSe'), { context, format: '1.2-2' }),
			createMeasureColumn('CE', att('cijferCe'), { context, format: '1.2-2' }),
			this.createAantalColumn(context),
		];
	}

	protected createAantalColumn(context: DashboardContext<CijferSeCeI, CijferSeCeA, VerschilSeCeComponent>): ColumnDef<DataRow<CijferSeCeA>> {
		return this.createLeerlingColumn('Cijfers', att('count_records'), { context }, att('leerlingen'));
	}

	override partitionBarData(rowRoot: Level<CijferSeCeA, number[]>): Path<CijferSeCeA, number[]>[][] {
		const path = rowRoot.r[0];
		if (!path) return [];
		const a = getLeafA(path);
		// genereer records met verschilAfgerond van -3.75, -3.5 t/m 3.5, 3.75
		const verschillen = [-3.75, ...range(-3.5, 4, 0.5), 3.75];
		return [verschillen.map((verschilAfgerond) => [...initial(path), { ...last(path)!, a: { ...a, verschilAfgerond } }])];
	}

	override makeBar(
		attrs: CijferSeCeI,
		path: Path<CijferSeCeA, number[]>,
		_context: DashboardContext<CijferSeCeI, CijferSeCeA, VerschilSeCeComponent>
	): BarInfo {
		const a = last(path)!.a;
		const verschilAfgerond = a.verschilAfgerond;

		if (verschilAfgerond === null) return { size: 0 };
		const verschilString = verschilAfgerond.toString().replace('-', 'minus_').replace('.', '_');
		const size: number = get(attrs, 'cf_m_nr_verschil_' + verschilString, 0);
		const className = generateCssClassForString(verschilString);

		return {
			size,
			className,
			tooltip: this.createTooltip(a, size),
		};
	}

	createTooltip({ verschilAfgerond }: CijferSeCeA, size: number): TooltipType {
		let tooltip = 'Aantal ';
		if (verschilAfgerond === null) {
			tooltip += 'onbekend: ';
		} else if (verschilAfgerond === 0) {
			tooltip += 'SE-cijfers gelijk aan het bijbehorende CE-cijfer: ';
		} else {
			const toolTipMeerDan = verschilAfgerond === -3.75 || verschilAfgerond === 3.75 ? 'meer dan ' : '';
			const formattedNumber = formatNumber(Math.abs(verschilAfgerond), 'nl-NL', '1.0-2');
			const hogerOfLager = verschilAfgerond < 0 ? 'lager' : 'hoger';

			tooltip += `SE-cijfers ${toolTipMeerDan}${formattedNumber} punt${
				Math.abs(verschilAfgerond) >= 2 ? 'en' : ''
			} ${hogerOfLager} dan het bijbehorende CE-cijfer: `;
		}
		tooltip += `${size}`;
		return tooltip;
	}

	override getSelectionConfig(context: DataRequestContext<CijferSeCeA>): SelectionConfig<CijferSeCeA> | undefined {
		return { ...super.getSelectionConfig(context)!, getSize: att('leerlingen') };
	}

	override getRowClickUrl(
		context: DashboardContext<CijferSeCeI, CijferSeCeA, DataTreeTableConfig<CijferSeCeI, CijferSeCeA>>,
		path: Path<CijferSeCeA, number[]>
	): UrlTree | undefined {
		const atts: Att[] = ['cf_fk_vk.vk_nm_kernvak', 'cf_nm_vak', 'cf_nm_lesgroep_docenten'];
		const groups = this.variant() === DashboardVariant.ACTUEEL ? context.groupNames : context.groupNames.slice(0, -1);
		return this.getZoomClickUrl(atts, groups, path, { singleGroup: true }) ?? super.getRowClickUrl(context, path);
	}

	override getBarchartQty(path: Path<CijferSeCeA, number[]>): number | null {
		return getLeafA(path).cijferVerschilSeCe ?? 0;
	}

	protected areas = [
		{ qty: -1, className: 'dark' },
		{ qty: -0.5, className: 'light' },
		{ qty: 0, className: 'none' },
		{ qty: 0.5, className: 'none' },
		{ qty: 1, className: 'light' },
		{ className: 'dark' },
	];

	override createXAxis(context: DashboardContext<CijferSeCeI, CijferSeCeA, VerschilSeCeComponent>): Axis {
		const min = Math.min(context.dataRoot!.a.minVerschil ?? 0, 0);
		const max = Math.max(context.dataRoot!.a.maxVerschil ?? 0, 0);
		return { min, max, areas: this.areas, ticks: this.createTicks(min, max) };
	}

	private createTicks(min: number, max: number) {
		// we willen maximaal 5 ticks (het exacte aantal hangt af van afronding)
		const nSteps = 5;
		// het bereik is min tot max
		// als min positief is of max negatief, wordt het bereik opgerekt zodat 0 er deel van uitmaakt
		const delta = Math.max(max, 0) - Math.min(min, 0);
		// de gewenste stapgrootte is (delta / nSeps), maar dan afgerond
		// zoek een afronding die geschikt is voor dit bereik
		const b = Math.pow(10, Math.round(Math.log10(delta / nSteps))) / 2;
		// rond de stapgrootte af naar het volgende b-tal
		const step = b * Math.ceil(delta / nSteps / b);

		const qties: number[] = [];
		// bij een negatieve min gaan we terug vanaf 0 naar min, maar dan omgekeerd
		if (min < 0) qties.push(...range(-step, min, -step).reverse());
		// we gaan altijd vanaf 0 naar max
		qties.push(...range(0, max, step));

		return qties.map((qty) => ({
			qty,
			label: (qty > 0 ? '+' : '') + formatNumber(qty, 'nl-NL', '1.2-2'),
			// alleen lijnen voor marks die niet samenvallen met een shade of de y-as
			showLine: ![-1, -0.5, 0, 0.5, 1].includes(qty),
		}));
	}

	getSchooljarenCaption() {
		return this.schooljaar ? getOnderwijsresultatenSchooljaarRangeString(this.schooljaar, this.aantalJaren, 'cv') : '';
	}

	override getDisplayOptions(): ExportFilter[] {
		return [
			{ label: 'Indicator over', value: indicatorOverOpties[this.aantalJaren] },
			{ label: 'Meetellende vestigingen', value: this.uitsluiten() ? 'Minimaal 30 cijfers' : 'Alle' },
		];
	}
}
